/**
 * Which Flagsmith environment the privileges of this tenant come from.
 *
 * Shown even when everything is fine: with several tenants in play, knowing which environment the
 * flag list belongs to is the difference between gating an item correctly and hiding it from
 * everyone.
 *
 * @module Pages/BUs/WhatsNew
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import { UTButton } from '@group-link-one/gl-ui/utilities';
import type { JSX } from 'react';
import { maskEnvironmentKey } from '../../../Utils/maskEnvironmentKey';
import { IconButton } from '@/Shared/components/IconButton/IconButton';

const barStyle = css`
  display: flex;
  align-items: center;
  gap: var(--space-s);
  padding: var(--space-s) var(--space-m);
  background: var(--color-surface-sunken);
  border-radius: var(--radius-m);
  flex-wrap: wrap;
`;

const labelStyle = css`
  font-size: 10px;
  font-weight: var(--font-weight-bold);
  letter-spacing: var(--letter-spacing-caps);
  text-transform: uppercase;
  color: var(--color-text-muted);
`;

const slugStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text);
`;

const keyStyle = css`
  font-family: var(--font-mono);
  font-size: var(--font-size-2xs);
  color: var(--color-text-muted);
`;

const spacerStyle = css`
  flex: 1;
`;

const statusStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--color-text-secondary);
`;

const warningStyle = css`
  font-size: var(--font-size-2xs);
  color: var(--gl-yellow-dark);
`;

export interface FlagsBarProps {
  tenantSlug: string;
  environmentKey?: string | null;
  flagCount: number;
  isLoading: boolean;
  isMissingKey: boolean;
  onReload: () => void;
  onOpenBU: () => void;
}

export const FlagsBar = ({
  tenantSlug,
  environmentKey,
  flagCount,
  isLoading,
  isMissingKey,
  onReload,
  onOpenBU,
}: FlagsBarProps): JSX.Element => (
  <div className={barStyle}>
    <GLIcon variant='Misc.Broadcast' size={14} fill='var(--color-text-muted)' />
    <span className={labelStyle}>Flags de</span>
    <span className={slugStyle}>{tenantSlug}</span>
    {!isMissingKey && environmentKey ? (
      <span className={keyStyle}>{maskEnvironmentKey(environmentKey)}</span>
    ) : null}

    <div className={spacerStyle} />

    {isMissingKey ? (
      <>
        <span className={warningStyle}>cadastre a key na BU para listar as flags</span>
        <UTButton fitContent variant='tertiary' size='small' text='Abrir BU' onClick={onOpenBU} />
      </>
    ) : (
      <>
        <span className={statusStyle}>
          {isLoading ? 'carregando flags…' : `${flagCount} flags carregadas`}
        </span>
        <IconButton
          icon='Arrows.ArrowsClockwise'
          size='sm'
          aria-label='Recarregar flags'
          onClick={onReload}
        />
      </>
    )}
  </div>
);
